<template>
  <div id="page-user-list">
    <vx-card
      ref="filterCard"
      title="Filters"
      class="user-list-filters mb-8"
      actionButtons
      @refresh="resetColFilters"
      @remove="resetColFilters"
    >
      <div class="vx-row">
        <div class="vx-col md:w-1/4 sm:w-1/2 w-full">
          <label class="text-sm opacity-75">Client</label>
          <v-select
            :options="clientOptions"
            :clearable="false"
            :dir="$vs.rtl ? 'rtl' : 'ltr'"
            v-model="clientFilter"
            class="mb-4 md:mb-0"
          />
        </div>
        <div class="vx-col md:w-1/4 sm:w-1/2 w-full">
          <label class="text-sm opacity-75">Status</label>
          <v-select
            :options="statusOptions"
            :clearable="false"
            :dir="$vs.rtl ? 'rtl' : 'ltr'"
            v-model="statusFilter"
            class="mb-4 md:mb-0"
          />
        </div>
        <div class="vx-col md:w-1/4 sm:w-1/2 w-full">
          <label class="text-sm opacity-75">Validité</label>
          <v-select
            :options="validityOptions"
            :clearable="false"
            :dir="$vs.rtl ? 'rtl' : 'ltr'"
            v-model="validityFilter"
          />
        </div>
      </div>
    </vx-card>

    <div class="vx-card p-6">
      <div class="flex flex-wrap items-center">
        <div class="flex-grow">
          <vs-dropdown vs-trigger-click class="cursor-pointer">
            <div
              class="p-4 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bg cursor-pointer flex items-center justify-between font-medium"
            >
              <span class="mr-2"
                >{{
                  (currentPage - 1) * paginationPageSize + 1
                }}
                -
                {{
                  currentPage * paginationPageSize > totalProducts
                    ? totalProducts
                    : currentPage * paginationPageSize
                }}
                of {{ totalProducts }}</span
              >
              <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
            </div>
            <vs-dropdown-menu>
              <vs-dropdown-item @click="changePageSize(10)">
                <span>10</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="changePageSize(20)">
                <span>20</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="changePageSize(25)">
                <span>25</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="changePageSize(30)">
                <span>30</span>
              </vs-dropdown-item>
            </vs-dropdown-menu>
          </vs-dropdown>
        </div>

        <vs-button class="mx-4 mb-4 md:mb-0" @click="gridApi.exportDataAsCsv()"
          >Export as CSV</vs-button
        >
        <vs-button class="mx-4 mb-4 md:mb-0" @click="submit"
          >Transaction</vs-button
        >
        <vs-button class="mx-4 mb-4 md:mb-0" @click="deposit">Dépot</vs-button>
        <vs-button class="mx-4 mb-4 md:mb-0" @click="labels">Labels</vs-button>
        <vs-button 
          class="mx-4 mb-4 md:mb-0" 
          color="danger"
          @click="deleteSelectedProducts"
        >Supprimer</vs-button>

        <div class="flex items-center sm:mr-4 mr-0 sm:order-normal order-3 sm:mt-0 mt-4">
          <vs-input
            class="sm:w-auto w-full"
            v-model="searchQuery"
            @keyup.enter="performSearch"
            placeholder="Search..."
          />
          <vs-button
            class="ml-2"
            icon="search"
            @click="performSearch"
          ></vs-button>
        </div>
      </div>

      <ag-grid-vue
        ref="agGridTable"
        :components="components"
        :gridOptions="gridOptions"
        class="ag-theme-material w-100 my-4 ag-grid-table"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="productsData"
        rowSelection="multiple"
        colResizeDefault="shift"
        :animateRows="true"
        :floatingFilter="false"
        :pagination="false"
        :enableRtl="$vs.rtl"
        @grid-ready="onGridReady"
      >
      </ag-grid-vue>

      <vs-pagination 
        :total="totalPages" 
        :max="7" 
        :value="currentPage"
        @input="handlePageChange"
      />
    </div>

    <vs-prompt
      class="calendar-event-dialog"
      title="Ajouter Transaction"
      accept-text="Ajouter"
      @accept="addTransaction"
      :active.sync="activePromptAddEvent"
    >
      <div class="mt-4">
        <label class="vs-input--label">Transaction Type</label>
        <v-select
          v-model="transaction_type"
          :clearable="false"
          :options="transactionOptions"
          v-validate="'required'"
          name="transaction"
          :dir="$vs.rtl ? 'rtl' : 'ltr'"
        />
        <span class="text-danger text-sm" v-show="errors.has('transaction')">{{
          errors.first("transaction")
        }}</span>
      </div>
    </vs-prompt>
    <div class="demo-alignment">
      <vs-popup fullscreen title="fullscreen" :active.sync="popupActive">
        <div v-if="popupActive">
          <Contract :products="depositProducts" :client="selectedClient" />
        </div>
      </vs-popup>
      <vs-popup fullscreen title="fullscreen" :active.sync="labelsPopupActive">
        <div v-if="labelsPopupActive">
          <Labels :products="labelsProducts" />
        </div>
      </vs-popup>
    </div>
  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";
import vSelect from "vue-select";

import CellRendererLink from "../../layouts/components/cell-renderer/CellRendererLink.vue";
import CellRendererStatus from "../../layouts/components/cell-renderer/CellRendererStatus.vue";
import CellRendererProductActions from "../../layouts/components/cell-renderer/CellRendererProductActions.vue";
import Contract from "../pages/Contract.vue";
import Labels from "../pages/Labels.vue";

export default {
  components: {
    AgGridVue,
    vSelect,
    Contract,
    Labels,

    CellRendererLink,
    CellRendererStatus,
    CellRendererProductActions,
  },
  data() {
    return {
      depositProducts: [],

      popupActive: false,
      labelsPopupActive: false,
      labelsProducts: [],

      activePromptAddEvent: false,

      transaction_type: null,

      transactionOptions: [],

      clientFilter: { label: "All", value: "all" },
      clientOptions: [{ label: "All", value: "all" }],

      statusFilter: { label: "All", value: "all" },
      statusOptions: [
        { label: "All", value: "all" },
        { label: "Disponible", value: "Disponible" },
        { label: "Vendu", value: "Vendu" },
        { label: "Client payé", value: "Client payé" },
        { label: "Retour", value: "Retour" },
        { label: "Pickup", value: "Pickup" },
        { label: "Association", value: "Association" },
      ],

      validityFilter: { label: "All", value: "all" },
      validityOptions: [
        { label: "All", value: "all" },
        { label: "30 Jours", value: "30" },
        { label: "60 Jours", value: "60" },
      ],

      searchQuery: "",
      pageSize: 25,

      gridApi: null,
      gridOptions: {
        rowClassRules: {
          "tr-table-state-warning": (params) =>
            params.api.getValue("status", params.node) === "Vendu",
          "tr-table-state-success": (params) =>
            params.api.getValue("status", params.node) === "Client payé",
          "tr-table-state-primary": (params) =>
            params.api.getValue("status", params.node) === "Association",
          "tr-table-state-danger": (params) =>
            params.api.getValue("status", params.node) === "Retour" ||
            params.api.getValue("status", params.node) === "Pickup",
        },
      },
      defaultColDef: {
        sortable: true,
        resizable: true,
        suppressMenu: true,
      },
      columnDefs: [
        {
          headerName: "ID",
          field: "ref",
          width: 150,
          filter: false,
          checkboxSelection: true,
          headerCheckboxSelectionFilteredOnly: true,
          headerCheckboxSelection: true,
        },
        {
          headerName: "Name",
          field: "name",
          filter: false,
          width: 230,
        },
        {
          headerName: "Client price",
          field: "client_price",
          filter: false,
          width: 140,
        },
        {
          headerName: "Sale price",
          field: "sale_price",
          filter: false,
          width: 140,
        },
        {
          headerName: "Benefit",
          field: "benefit",
          filter: false,
          width: 140,
        },
        {
          headerName: "Percentage",
          field: "percentage",
          filter: false,
          width: 140,
        },
        {
          headerName: "Validity",
          field: "validity",
          filter: false,
          width: 140,
        },
        {
          headerName: "Client",
          field: "client.display",
          filter: false,
          width: 150,
        },
        {
          headerName: "Status",
          field: "status",
          filter: false,
          width: 150,
          cellRendererFramework: "CellRendererStatus",
        },
        {
          headerName: "Actions",
          field: "transactions",
          width: 150,
          cellRendererFramework: "CellRendererProductActions",
        },
      ],
      components: {
        CellRendererLink,
        CellRendererStatus,
        CellRendererProductActions,
      },
    };
  },
  watch: {
    clientFilter() {
      // Only trigger if component is mounted (prevents initialization triggers)
      if (this.$el) {
        this.fetchProducts(1); // Reset to first page on filter change
      }
    },
    statusFilter() {
      // Only trigger if component is mounted (prevents initialization triggers)
      if (this.$el) {
        this.fetchProducts(1); // Reset to first page on filter change
      }
    },
    validityFilter() {
      // Only trigger if component is mounted (prevents initialization triggers)
      if (this.$el) {
        this.fetchProducts(1); // Reset to first page on filter change
      }
    },
  },
  computed: {
    productsData() {
      return this.$store.getters.getProducts;
    },
    paginationPageSize() {
      return this.pageSize;
    },
    totalPages() {
      const pages = this.$store.getters.getTotalProductPages;
      // Ensure it's a number and valid
      const numPages = typeof pages === 'number' ? pages : parseInt(pages) || 1;
      return numPages > 0 ? numPages : 1;
    },
    totalProducts() {
      return this.$store.getters.getTotalProducts;
    },
    currentPage() {
      return Number(this.$store.getters.getCurrentProductPage) || 1;
    },
  },
  methods: {
    async fetchProducts(page = 1) {
      try {
        await this.$store.dispatch("PRODUCTS_REQUEST", {
          page,
          limit: this.pageSize,
          client: this.clientFilter.value,
          status: this.statusFilter.value,
          validity: this.validityFilter.value,
          search: this.searchQuery,
        });
      } catch (error) {
        this.$vs.notify({
          title: "Erreur",
          text: "Erreur lors du chargement des produits",
          color: "danger",
          position: "top-center",
        });
      }
    },
    changePageSize(newSize) {
      this.pageSize = newSize;
      this.currentPage = 1; // Reset to first page
      this.fetchProducts(1);
    },
    handlePageChange(page) {
      const pageNum = Number(page);
      if (pageNum !== this.currentPage) {
        this.fetchProducts(pageNum);
      }
    },
    submit() {
      const selectedProducts = this.gridApi.getSelectedRows();
      if (selectedProducts.length) {
        if (
          selectedProducts.every((val, i, arr) => val.status === arr[0].status)
        ) {
          if (selectedProducts[0].status === "Disponible") {
            this.transaction_type = {
              label: "Vente de produit",
              value: "Vente de produit",
            };
            this.transactionOptions = [
              { label: "Vente de produit", value: "Vente de produit" },
              { label: "Retour produit", value: "Retour produit" },
            ];
            this.activePromptAddEvent = true;
          } else if (selectedProducts[0].status === "Vendu") {
            this.transaction_type = {
              label: "Paiement client",
              value: "Paiement client",
            };
            this.transactionOptions = [
              { label: "Paiement client", value: "Paiement client" },
            ];
            this.activePromptAddEvent = true;
          } else if (selectedProducts[0].status === "Retour") {
            this.transaction_type = {
              label: "Pickup produit",
              value: "Pickup produit",
            };
            this.transactionOptions = [
              { label: "Pickup produit", value: "Pickup produit" },
              { label: "Don association", value: "Don association" },
              { label: "Vente de produit", value: "Vente de produit" },
            ];
            this.activePromptAddEvent = true;
          } else {
            this.transaction_type = null;
            this.transactionOptions = [];
            this.$vs.notify({
              title: "Info",
              text: "Aucune action ne peut être faite à ce stade",
              color: "warning",
              position: "top-center",
            });
          }
        } else {
          this.$vs.notify({
            title: "Erreur",
            text: "Les produits sélectionnés n'ont pas le même statut",
            color: "danger",
            position: "top-center",
          });
        }
      } else {
        this.$vs.notify({
          title: "Erreur",
          text: "Aucun produit sélectionné",
          color: "danger",
          position: "top-center",
        });
      }
    },
    deposit() {
      const selectedProducts = this.gridApi.getSelectedRows();
      if (!selectedProducts.length) {
        this.$vs.notify({
          title: "Erreur",
          text: "Aucun produit sélectionné",
          color: "danger",
          position: "top-center",
        });
        return;
      }

      if (
        !selectedProducts.every(
          (val, i, arr) => val.client.ref === arr[0].client.ref
        )
      ) {
        this.$vs.notify({
          title: "Erreur",
          text: "Le produits n'appartienent pas au même client",
          color: "danger",
          position: "top-center",
        });
        return;
      }

      this.popupActive = true;
      this.depositProducts = selectedProducts;
      this.selectedClient = selectedProducts[0].client;
    },
    labels() {
      const selectedProducts = this.gridApi.getSelectedRows();
      if (!selectedProducts.length) {
        this.$vs.notify({
          title: "Erreur",
          text: "Aucun produit sélectionné",
          color: "danger",
          position: "top-center",
        });
        return;
      }

      this.labelsPopupActive = true;
      this.labelsProducts = selectedProducts;
    },
    addTransaction() {
      const selectedProducts = this.gridApi.getSelectedRows();
      // const { clientFilter, statusFilter, validityFilter } = this;
      this.$store
        .dispatch("ADD_TRANSACTION", {
          products: selectedProducts,
          type: this.transaction_type.value,
        })
        .then(async () => {
          await this.fetchProducts(this.currentPage);
          this.transaction_type = null;
          this.$vs.notify({
            title: "Success",
            text: "Transaction ajouté avec succès",
            color: "success",
            position: "top-center",
          });
        })
        .catch(() => {
          this.$vs.notify({
            title: "Erreur",
            text: "Une erreur s'est produite",
            color: "danger",
            position: "top-center",
          });
        });
    },
    resetColFilters() {
      this.clientFilter =
        this.statusFilter =
        this.validityFilter =
          { label: "All", value: "all" };
      this.searchQuery = "";
      this.fetchProducts(1);
      this.$refs.filterCard.removeRefreshAnimation();
    },
    performSearch() {
      this.fetchProducts(1);
    },
    deleteSelectedProducts() {
      const selectedProducts = this.gridApi.getSelectedRows();
      if (!selectedProducts.length) {
        this.$vs.notify({
          title: "Erreur",
          text: "Aucun produit sélectionné",
          color: "danger",
          position: "top-center",
        });
        return;
      }

      const productCount = selectedProducts.length;

      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: "Confirmer la suppression",
        text: `Vous êtes sur le point de supprimer ${productCount} produit${productCount > 1 ? "s" : ""}. Cette action est irréversible.`,
        accept: async () => {
          try {
            // const { clientFilter, statusFilter, validityFilter } = this;
            const deletePromises = selectedProducts.map(product =>
              this.$store.dispatch("DELETE_PRODUCT", { _id: product._id })
            );
            
            await Promise.all(deletePromises);
            
            await this.fetchProducts(this.currentPage);
            
            this.$vs.notify({
              title: "Succès",
              text: `${productCount} produit${productCount > 1 ? "s" : ""} supprimé${productCount > 1 ? "s" : ""} avec succès`,
              color: "success",
              position: "top-center",
            });
          } catch (error) {
            this.$vs.notify({
              title: "Erreur",
              text: "Une erreur s'est produite lors de la suppression",
              color: "danger",
              position: "top-center",
            });
          }
        },
        acceptText: "Supprimer",
      });
    },
    onGridReady(params) {
      // Properly initialize grid API when grid is ready
      this.gridApi = params.api;

      // Handle RTL layout adjustment after grid is ready
      if (this.$vs.rtl) {
        this.$nextTick(() => {
          const header = this.$refs.agGridTable.$el.querySelector(
            ".ag-header-container"
          );
          if (header) {
            header.style.left =
              "-" + String(Number(header.style.transform.slice(11, -3)) + 9) + "px";
          }
        });
      }
    },
  },
  beforeDestroy() {
    // Clean up AG Grid instance to prevent memory leaks
    if (this.gridApi) {
      // Clear any event listeners if needed
      // AG Grid Vue automatically destroys the grid when component is destroyed
      // but we clear the reference to prevent further usage
      this.gridApi = null;
    }
    // Clear gridOptions reference
    if (this.gridOptions) {
      this.gridOptions.api = null;
    }
  },
  async beforeMount() {
    // Fetch initial products first (priority)
    await this.fetchProducts(1);
    
    // Fetch all clients for the filter dropdown in the background (non-blocking)
    this.$store.dispatch("CLIENTS_REQUEST_LIGHTWEIGHT").then(() => {
      const allClients = this.$store.getters.getAllClientsForFilters;
      this.clientOptions = [
        { label: "All", value: "all" },
        ...allClients.map((client) => ({
          label: client.display,
          value: client.display,
        })),
      ];
    }).catch(() => {
      // If client fetch fails, use clients from current products as fallback
      this.clientOptions = [
        { label: "All", value: "all" },
        ...this.$store.getters.getProducts
          .map((product) => product.client && product.client.display)
          .filter((val, i, arr) => val && arr.indexOf(val) === i)
          .map((client) => ({
            label: client,
            value: client,
          })),
      ];
    });
  },
  created() {},
};
</script>

<style lang="scss">
.row-fail {
  font-weight: bold;
  background-color: #aa2e25 !important;
}

.row-pass {
  font-weight: bold;
  background-color: #357a38 !important;
}

#page-user-list {
  .user-list-filters {
    .vs__actions {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-58%);
    }
  }
}

</style>
